(() => {
  const resultLimit = 10;
  let searchIndexPromise;

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr');

  const loadSearchIndex = () => {
    if (searchIndexPromise) return searchIndexPromise;
    const indexUrl = document.body.dataset.medixoSearchIndex;
    if (!indexUrl) return Promise.reject(new Error('BIOMEVIA search index URL missing'));

    searchIndexPromise = fetch(indexUrl, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error('BIOMEVIA search index unavailable');
        return response.json();
      })
      .then((products) => products.map((product) => ({
        ...product,
        normalizedTitle: normalize(product.t),
        normalizedVendor: normalize(product.v),
        normalizedCategory: normalize(product.c)
      })))
      .catch((error) => {
        searchIndexPromise = null;
        throw error;
      });

    return searchIndexPromise;
  };

  const findProducts = (index, term) => {
    const query = normalize(term);
    const matches = [];

    index.forEach((product) => {
      const titlePosition = product.normalizedTitle.indexOf(query);
      const wordStartsWithQuery = product.normalizedTitle
        .split(/[\s\-/()]+/)
        .some((word) => word.startsWith(query));
      let score = Number.POSITIVE_INFINITY;

      if (product.normalizedTitle.startsWith(query)) score = 0;
      else if (wordStartsWithQuery) score = 10 + Math.max(titlePosition, 0);
      else if (titlePosition !== -1) score = 30 + titlePosition;
      else if (product.normalizedVendor.includes(query)) score = 60 + product.normalizedVendor.indexOf(query);
      else if (product.normalizedCategory.includes(query)) score = 80 + product.normalizedCategory.indexOf(query);

      if (Number.isFinite(score)) matches.push({ product, score });
    });

    matches.sort((first, second) => (
      first.score - second.score
      || first.product.t.localeCompare(second.product.t, 'fr', { sensitivity: 'base' })
    ));

    return {
      total: matches.length,
      products: matches.slice(0, resultLimit).map(({ product }) => product)
    };
  };

  const appendHighlightedText = (element, value, term) => {
    const text = String(value || '');
    const position = normalize(text).indexOf(normalize(term));
    if (position === -1) {
      element.textContent = text;
      return;
    }

    element.append(document.createTextNode(text.slice(0, position)));
    const mark = document.createElement('mark');
    mark.textContent = text.slice(position, position + term.length);
    element.append(mark, document.createTextNode(text.slice(position + term.length)));
  };

  const createProductResult = (product, term) => {
    const link = document.createElement('a');
    link.className = 'medixo-predictive__item';
    link.href = product.u;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');

    const media = document.createElement('span');
    media.className = 'medixo-predictive__image';
    if (product.i) {
      const image = document.createElement('img');
      image.src = product.i;
      image.alt = '';
      image.loading = 'lazy';
      media.appendChild(image);
    } else {
      const fallback = document.createElement('span');
      fallback.textContent = 'BIOMEVIA';
      media.appendChild(fallback);
    }

    const copy = document.createElement('span');
    copy.className = 'medixo-predictive__copy';
    const title = document.createElement('strong');
    appendHighlightedText(title, product.t, term);
    const meta = document.createElement('small');
    meta.textContent = product.v || product.c || 'Produit dentaire';
    copy.append(title, meta);
    link.append(media, copy);
    return link;
  };

  document.querySelectorAll('[data-medixo-predictive]').forEach((container) => {
    const originalInput = container.querySelector('[data-predictive-input]');
    const panel = container.querySelector('[data-predictive-panel]');
    const results = container.querySelector('[data-predictive-results]');
    const status = container.querySelector('[data-predictive-status]');
    if (!originalInput || !panel || !results || !status) return;

    // The v2.1 input is cloned so its former native-search listeners are removed
    // without changing the existing mobile header, menu or cart behaviour.
    const input = originalInput.cloneNode(true);
    originalInput.replaceWith(input);
    let timer;
    let activeIndex = -1;
    let searchSequence = 0;

    const close = () => {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    };

    const showLoading = () => {
      results.replaceChildren();
      const message = document.createElement('p');
      message.className = 'medixo-predictive__message';
      message.textContent = 'Recherche en cours…';
      results.appendChild(message);
      status.textContent = 'Recherche en cours';
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const render = ({ total, products }, term) => {
      results.replaceChildren();
      if (!products.length) {
        const empty = document.createElement('p');
        empty.className = 'medixo-predictive__message';
        empty.textContent = `Aucun produit ne contient « ${term} ».`;
        results.appendChild(empty);
        status.textContent = 'Aucun produit proposé';
      } else {
        const heading = document.createElement('p');
        heading.className = 'medixo-predictive__heading';
        heading.textContent = `${total.toLocaleString('fr-FR')} produit${total > 1 ? 's' : ''} contenant « ${term} »`;
        results.appendChild(heading);
        products.forEach((product) => results.appendChild(createProductResult(product, term)));
        if (total > products.length) {
          const hint = document.createElement('p');
          hint.className = 'medixo-predictive__hint';
          hint.textContent = '10 premières propositions — continuez à écrire pour affiner.';
          results.appendChild(hint);
        }
        status.textContent = `${total} produits correspondants`;
      }
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      activeIndex = -1;
    };

    const runSearch = async (term, sequence) => {
      showLoading();
      try {
        const index = await loadSearchIndex();
        if (sequence !== searchSequence || input.value.trim() !== term) return;
        render(findProducts(index, term), term);
      } catch (_error) {
        if (sequence === searchSequence) close();
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const term = input.value.trim();
      searchSequence += 1;
      if (!term.length) {
        close();
        return;
      }
      const sequence = searchSequence;
      timer = window.setTimeout(() => runSearch(term, sequence), 140);
    });

    input.addEventListener('keydown', (event) => {
      const links = [...results.querySelectorAll('a')];
      if (event.key === 'ArrowDown' && links.length) {
        event.preventDefault();
        activeIndex = (activeIndex + 1) % links.length;
      } else if (event.key === 'ArrowUp' && links.length) {
        event.preventDefault();
        activeIndex = (activeIndex - 1 + links.length) % links.length;
      } else if (event.key === 'Enter' && activeIndex >= 0 && links[activeIndex]) {
        event.preventDefault();
        links[activeIndex].click();
        return;
      } else if (event.key === 'Escape') {
        close();
        return;
      } else {
        return;
      }

      links.forEach((link, index) => {
        const active = index === activeIndex;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-selected', String(active));
      });
      links[activeIndex]?.scrollIntoView({ block: 'nearest' });
    });

    document.addEventListener('click', (event) => {
      if (!container.contains(event.target)) close();
    });
  });
})();
